import { useEffect, useState, useCallback } from "react";
import { Star, MapPin, ShieldCheck, Clock, Car, Languages, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/apiClient";

const AVAIL = ["", "Weekday", "Weekend"];

export default function Marketplace() {
  const [caregivers, setCaregivers] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [f, setF] = useState({ specialty: "", location: "", availability: "", language: "", transportation: false, min_years: "" });

  const load = useCallback(() => {
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v !== "" && v !== false) params[k] = v; });
    api.get("/caregivers", { params }).then((r) => {
      setCaregivers(r.data);
      if (specialties.length === 0) setSpecialties([...new Set(r.data.flatMap((c) => c.specialties || []))].sort());
    }).catch(() => {});
  }, [f, specialties.length]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="marketplace-page">
      <div className="bg-blue-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <p className="text-xs uppercase tracking-[0.18em] text-teal-400 font-semibold">T5 Connect</p>
          <h1 className="font-display text-4xl lg:text-5xl font-bold mt-2">Find the right caregiver</h1>
          <p className="text-blue-200/70 mt-2 max-w-xl">Search T5's verified caregiver network by location, availability, services and more.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 grid sm:grid-cols-2 lg:grid-cols-6 gap-3" data-testid="marketplace-filters">
          <div className="relative lg:col-span-2"><Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" /><Input placeholder="City (e.g. Columbus)" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="filter-location" className="pl-9" /></div>
          <select value={f.specialty} onChange={(e) => setF({ ...f, specialty: e.target.value })} data-testid="filter-specialty" className="h-10 rounded-lg border border-slate-200 px-3 text-sm"><option value="">Any service</option>{specialties.map((s) => <option key={s} value={s}>{s}</option>)}</select>
          <select value={f.availability} onChange={(e) => setF({ ...f, availability: e.target.value })} data-testid="filter-availability" className="h-10 rounded-lg border border-slate-200 px-3 text-sm">{AVAIL.map((a) => <option key={a} value={a}>{a || "Any availability"}</option>)}</select>
          <Input placeholder="Language" value={f.language} onChange={(e) => setF({ ...f, language: e.target.value })} data-testid="filter-language" />
          <div className="flex items-center gap-3">
            <Input type="number" placeholder="Min yrs" value={f.min_years} onChange={(e) => setF({ ...f, min_years: e.target.value })} data-testid="filter-years" className="w-24" />
            <label className="flex items-center gap-1.5 text-sm text-slate-600 whitespace-nowrap"><input type="checkbox" checked={f.transportation} onChange={(e) => setF({ ...f, transportation: e.target.checked })} data-testid="filter-transport" /> <Car className="w-4 h-4" /> Has car</label>
          </div>
        </div>

        <p className="text-sm text-slate-500 mt-4" data-testid="marketplace-count">{caregivers.length} caregiver{caregivers.length !== 1 ? "s" : ""} found</p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-4">
          {caregivers.map((c) => (
            <div key={c.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`caregiver-card-${c.id}`}>
              <div className="flex gap-4 p-5">
                <img src={c.image} alt={c.name} className="w-20 h-20 rounded-xl object-cover" />
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5"><h3 className="font-display font-bold text-blue-950">{c.name}</h3>{c.verified && <ShieldCheck className="w-4 h-4 text-teal-600" />}</div>
                  <p className="text-xs text-amber-600 flex items-center gap-1 mt-0.5"><Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" /> {c.rating} · {c.reviews} reviews</p>
                  <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><MapPin className="w-3.5 h-3.5" /> {c.location} · {c.years} yrs</p>
                </div>
              </div>
              <div className="px-5 pb-3 flex flex-wrap gap-1.5">
                {(c.specialties || []).slice(0, 3).map((s) => <span key={s} className="text-[11px] bg-teal-50 text-teal-700 rounded-full px-2 py-0.5">{s}</span>)}
              </div>
              <div className="px-5 pb-4 flex flex-wrap gap-3 text-xs text-slate-500">
                {c.availability && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {c.availability}</span>}
                {c.transportation && <span className="flex items-center gap-1"><Car className="w-3.5 h-3.5" /> Has car</span>}
                {(c.languages || []).length > 0 && <span className="flex items-center gap-1"><Languages className="w-3.5 h-3.5" /> {(c.languages || []).join(", ")}</span>}
              </div>
            </div>
          ))}
        </div>
        {caregivers.length === 0 && <div className="text-center py-16 text-slate-400" data-testid="marketplace-empty">No caregivers match those filters. Try widening your search.</div>}
      </div>
    </div>
  );
}
